import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useBookingScreenEntry } from '../lib/useBookingScreenEntry'
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { buildBookingSuccessPath, notifyPayNowCompleted } from '../utils/payNowTabHandoff'

export default function BookingPaymentSuccessState({ clubCode, module, sportType, children }) {
  const { t } = useOptionalPrivanaI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const sessionId = searchParams.get('session_id')
  const bookingId = searchParams.get('booking_id')
  const cancellationRequest = ['spa','fitness'].includes(module) && searchParams.get('payment') === 'cancelled' ? searchParams.get('request_key') : null
  const [status, setStatus] = useState((sessionId || cancellationRequest) && !bookingId ? 'pending' : 'ready')
  useBookingScreenEntry(`${module}:${status}`)
  const [error, setError] = useState(null)
  const [recheck, setRecheck] = useState(0)
  const [handoffComplete, setHandoffComplete] = useState(false)
  const [closeBlocked, setCloseBlocked] = useState(false)
  const [completedBookingId, setCompletedBookingId] = useState(null)

  useEffect(() => {
    if (bookingId) window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated'))
  }, [bookingId])

  useEffect(() => {
    if ((!sessionId && !cancellationRequest) || bookingId || !clubCode) return undefined
    let cancelled = false
    let attempts = 0
    let timer = null

    const poll = async () => {
      attempts += 1
      try {
        const res = cancellationRequest
          ? await memberPortalApiPost(`/api/members-portal/${clubCode}/booking-operations/${encodeURIComponent(cancellationRequest)}/reconcile`, { module })
          : await memberPortalApiGet(`/api/members-portal/${clubCode}/booking-payments/status?session_id=${encodeURIComponent(sessionId)}`)
        const json = await res.json().catch(() => null)
        if (cancelled) return
        if (res.ok && json?.status === 'completed' && json.booking_id) {
          window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated'))
          const next = new URLSearchParams(searchParams)
          next.delete('session_id')
          next.delete('payment'); next.delete('request_key')
          next.set('booking_id', json.booking_id)
          setCompletedBookingId(json.booking_id)
          notifyPayNowCompleted({ type: 'booking_pay_now_completed', module, clubCode, booking_id: json.booking_id })
          if (window.opener && !window.opener.closed) {
            setHandoffComplete(true)
            setStatus('handoff')
            window.setTimeout(() => {
              window.close()
              setCloseBlocked(true)
            }, 600)
            return
          }
          navigate({ search: next.toString() }, { replace: true })
          setStatus('ready')
          return
        }
        if (res.ok && json?.status === 'cancelled' && json?.safe_to_retry && ['spa','fitness'].includes(module)) { setStatus('cancelled'); return }
        if (res.ok && json?.status === 'needs_help' && ['spa','fitness'].includes(module)) { setError('facilityBooking.recoveryHelp'); setStatus('failed'); return }
        if (res.ok && json?.status === 'failed') {
          setError("portalPresentation.member_payment_was_received_but_we_could_not_confirm_the_booking_automatically_please_contact_the_club")
          setStatus('failed')
          return
        }
      } catch (pollError) {
        if (!cancelled) setError("portalPresentation.member_still_confirming_your_payment_this_may_take_a_few_moments")
      }
      if (!cancelled && attempts < 60) timer = window.setTimeout(poll, attempts < 6 ? 1500 : 3000)
    }

    poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [bookingId, clubCode, memberPortalApiGet, memberPortalApiPost, module, navigate, searchParams, sessionId, cancellationRequest, recheck])

  if (status === 'ready') return children

  if (handoffComplete || status === 'handoff') {
    const returnPath = buildBookingSuccessPath({ module, clubCode, bookingId: completedBookingId || searchParams.get('booking_id') || '', sportType })
    return (
      <div className="mp-page mp-page--center">
        <div className="mp-card mp-card--center mp-booking-success">
          <Loader2 size={42} className="mp-success-icon mp-spin" />
          <h2> {t("portalPresentation.member_returning_you_to_your_booking")} </h2>
          <p className="mp-text-muted"> {t("portalPresentation.member_your_booking_is_confirmed_you_can_close_this_checkout_tab_and_continue_in_your_original_booking_tab")} </p>
          {closeBlocked ? <a className="mp-btn mp-btn--primary" href={returnPath}> {t("portalPresentation.member_return_to_booking_confirmation")} </a> : null}
        </div>
      </div>
    )
  }

  if (status === 'cancelled') return <div className="mp-page mp-page--center"><div className="mp-card mp-card--center mp-booking-success">
    <h2>{t('facilityBooking.checkoutCancelled')}</h2><p>{t('facilityBooking.checkoutCancelledHelp')}</p>
    <button className="mp-btn mp-btn--primary" onClick={() => { if (window.opener && !window.opener.closed) window.close(); else navigate(`/${clubCode}/${module}`) }}>{t('facilityBooking.backToBooking')}</button>
  </div></div>

  if (['spa','fitness'].includes(module)) return <div className="mp-page mp-page--center"><div className="mp-card mp-card--center mp-booking-success">
    <h2>{t(`facilityBooking.${status === 'failed' ? 'failed' : 'recoveryChecking'}`)}</h2>
    <p className="mp-warning">{t(`facilityBooking.${status === 'failed' ? 'recoveryHelp' : 'recoveryPending'}`)}</p>
    <button className="mp-btn mp-btn--secondary" onClick={() => { setStatus('pending'); setRecheck(value => value + 1) }}>{t('facilityBooking.recoveryCheck')}</button>
  </div></div>

  if (status === 'failed') {
    return (
      <div className="mp-page mp-page--center">
        <div className="mp-card mp-card--center mp-booking-success">
          <h2> {t("portalPresentation.member_we_could_not_confirm_your_booking")} </h2>
          <p className="mp-warning">{t(error || "portalPresentation.member_payment_was_received_but_the_booking_could_not_be_finalised_automatically_please_contact_the_club")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mp-page mp-page--center">
      <div className="mp-card mp-card--center mp-booking-success">
        <Loader2 size={42} className="mp-success-icon mp-spin" />
        <h2> {t("portalPresentation.member_confirming_your_payment")} </h2>
        <p className="mp-text-muted">{t("portalPresentation.member_your_payment_succeeded_we_are_finalising_your_booking_now")}</p>
        {error ? <p className="mp-warning">{t(error)}</p> : null}
      </div>
    </div>
  )
}
